// src/components/digital_twin/TwinAiExplainer.js
// --- UPDATE (Digital Twin MOW+ v1) — NEW FILE -----------------------------------------------
// The twin's "AI" sidebar tab:
//   • network health score with every deduction listed, live KPIs (reporting, anomalies, leaks,
//     risk, stale sensors, alerts);
//   • FlowSense findings (suspected leaks, server flood / anomaly predictions, high-risk assets,
//     ML anomalies, stale sensors) — each with Fly to / Open sensor;
//   • explainable asset risk ranking;
//   • AI explainer: "Explain this screen", "For the Ministry of Works", "Explain selection",
//     quick questions and Ask FlowSense (FlowSense AI engine, built-in rules when it is offline).
// No <form> elements (the panel can sit inside other forms) — Enter in the box asks.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Badge, Box, Button, Flex, HStack, Icon, IconButton, Input, Progress, SimpleGrid, Spinner, Text, Tooltip, VStack, Wrap, WrapItem, useColorModeValue, Collapse, useDisclosure,
} from '@chakra-ui/react';
import { FaBrain, FaCrosshairs, FaSatellite, FaTint, FaExclamationTriangle, FaUniversity, FaGraduationCap, FaPaperPlane, FaTimes, FaCopy, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { askFlowSenseAi } from '../../services/flowsenseAiService';
import { TWIN_QUESTIONS, answerTwinLocally, buildTwinContext, explainAsset, explainSensor, explainTwinForMinistry, explainTwinScreen } from './twinIntel';

const LEVEL = { critical: 'red', warning: 'orange', info: 'blue' };

const Kpi = memo(({ label, value, color, hint }) => {
    const bg = useColorModeValue('white', 'whiteAlpha.50');
    const bd = useColorModeValue('gray.200', 'whiteAlpha.200');
    const muted = useColorModeValue('gray.500', 'gray.400');
    return (
        <Tooltip label={hint} hasArrow isDisabled={!hint}>
            <Box bg={bg} borderWidth="1px" borderColor={bd} borderRadius="lg" p={2} textAlign="center">
                <Text fontSize="lg" fontWeight="900" color={color}>{value}</Text>
                <Text fontSize="2xs" fontWeight="700" color={muted} textTransform="uppercase" letterSpacing="wide">{label}</Text>
            </Box>
        </Tooltip>
    );
});
Kpi.displayName = 'TwinKpi';

function TwinAiExplainer({ intel, selectedAsset = null, selectedSensor = null, isLive = true, historicalTimestamp = null, simulation = null, onAction }) {
    const [history, setHistory] = useState([]);
    const [q, setQ] = useState('');
    const [asking, setAsking] = useState(false);
    const [showAllRisks, setShowAllRisks] = useState(false);
    const scoreInfo = useDisclosure();
    const mounted = useRef(true);
    React.useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

    const cardBg = useColorModeValue('white', 'gray.800');
    const tile = useColorModeValue('gray.50', 'whiteAlpha.50');
    const bd = useColorModeValue('gray.200', 'whiteAlpha.200');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const heading = useColorModeValue('gray.800', 'white');
    const aiBg = useColorModeValue('purple.50', 'rgba(128,90,213,0.12)');

    const s = intel?.summary || null;
    const scoreColor = s?.score === null || s?.score === undefined ? 'gray' : s.score >= 85 ? 'green' : s.score >= 65 ? 'yellow' : s.score >= 40 ? 'orange' : 'red';
    const findings = useMemo(() => intel?.findings || [], [intel]);
    const risks = useMemo(() => intel?.risks || [], [intel]);

    const push = useCallback((entry) => setHistory((h) => [{ at: Date.now() + Math.random(), ...entry }, ...h].slice(0, 6)), []);
    const ask = useCallback(async (question) => {
        const text = String(question || '').trim();
        if (!text || asking) return;
        setAsking(true);
        try {
            const r = await askFlowSenseAi(text, buildTwinContext(intel, { selected: selectedAsset, sensor: selectedSensor, isLive, historicalTimestamp, simulation }), 'digital_twin');
            const answer = r?.answer || r?.data?.answer || (typeof r === 'string' ? r : null);
            if (!answer) throw new Error('empty answer');
            if (mounted.current) push({ q: text, answer, source: 'ai', model: r?.model || r?.data?.model || null });
        } catch (_) {
            if (mounted.current) push({ q: text, answer: answerTwinLocally(text, intel, selectedAsset).answer, source: 'rules' });
        } finally {
            if (mounted.current) { setAsking(false); setQ(''); }
        }
    }, [asking, intel, selectedAsset, selectedSensor, isLive, historicalTimestamp, simulation, push]);

    const explainSelection = useCallback(() => {
        if (selectedSensor) {
            const row = intel?.sensorByKey?.get(String(selectedSensor.device_id ?? selectedSensor.id));
            push({ q: `Explain sensor ${selectedSensor.device_id ?? selectedSensor.id}`, answer: explainSensor(selectedSensor, row), source: 'rules' });
        } else if (selectedAsset) {
            push({ q: `Explain ${selectedAsset.name || `asset ${selectedAsset.id}`}`, answer: explainAsset(intel?.riskById?.get(String(selectedAsset.id))), source: 'rules' });
        }
    }, [intel, selectedAsset, selectedSensor, push]);

    const copy = useCallback(async (text) => { try { await navigator.clipboard.writeText(text); } catch (_) { /* selection fallback */ } }, []);

    const shownRisks = useMemo(() => (showAllRisks ? risks.slice(0, 25) : risks.slice(0, 6)), [risks, showAllRisks]);

    return (
        <VStack align="stretch" spacing={4} role="region" aria-label="FlowSense AI for the digital twin">
            {/* Health */}
            <Box bg={cardBg} borderWidth="1px" borderColor={bd} borderRadius="xl" p={4} boxShadow="sm">
                <HStack justify="space-between" mb={2}>
                    <HStack spacing={2}><Icon as={FaBrain} color="purple.400" /><Text fontWeight="800" color={heading}>FlowSense AI · Network</Text></HStack>
                    <Badge colorScheme={isLive ? 'green' : 'purple'} borderRadius="full">{isLive ? 'LIVE' : 'REPLAY'}</Badge>
                </HStack>
                <Flex align="center" gap={4}>
                    <Box textAlign="center" minW="84px">
                        <Text fontSize="3xl" fontWeight="900" color={`${scoreColor}.400`} lineHeight="1" aria-label="Network health score">{s?.score === null || s?.score === undefined ? '—' : s.score}</Text>
                        <Text fontSize="2xs" fontWeight="700" color={muted}>HEALTH / 100</Text>
                    </Box>
                    <Box flex={1}>
                        <Progress value={s?.score ?? 0} colorScheme={scoreColor} size="sm" borderRadius="full" mb={1} />
                        <Text fontSize="xs" color={muted}>
                            {s ? `${s.reporting}/${s.sensors} sensors reporting · ML learned ${s.trained} stream(s) · ${s.scored} asset(s) scored by the predictive model` : 'Loading…'}
                        </Text>
                        <Button size="xs" variant="link" colorScheme="purple" mt={1} rightIcon={<Icon as={scoreInfo.isOpen ? FaChevronUp : FaChevronDown} />} onClick={scoreInfo.onToggle} isDisabled={!s?.scoreDrivers}>How is this calculated?</Button>
                    </Box>
                </Flex>
                <Collapse in={scoreInfo.isOpen} animateOpacity>
                    <Box mt={2} p={2} bg={tile} borderRadius="md" fontSize="xs">
                        <Text>Starts at 100. Deductions:</Text>
                        {(s?.scoreDrivers || []).length ? s.scoreDrivers.map((x) => <Text key={x.label}>• {x.label}: {x.points}</Text>) : <Text>• none</Text>}
                    </Box>
                </Collapse>
                <SimpleGrid columns={3} spacing={2} mt={3}>
                    <Kpi label="Anomalies" value={s ? s.anomalies : '—'} color={s?.anomalies ? 'orange.400' : 'green.400'} hint="Readings far from the sensor's learned normal (z-score ≥ 3)" />
                    <Kpi label="Leaks" value={s ? s.leaks : '—'} color={s?.leaks ? 'red.400' : 'green.400'} hint="Pipelines whose pressure sensors dropped sharply" />
                    <Kpi label="High risk" value={s ? s.highRisk : '—'} color={s?.highRisk ? 'red.400' : 'green.400'} hint="Assets with a risk score of 70+" />
                    <Kpi label="Stale" value={s ? s.stale : '—'} color={s?.stale ? 'orange.400' : 'green.400'} hint="No reading for 15+ minutes" />
                    <Kpi label="Alerts" value={s ? s.openAlerts : '—'} color={s?.openAlerts ? 'red.400' : 'green.400'} hint="Open active sensor alerts" />
                    <Kpi label="Issues" value={s ? s.issues : '—'} color={s?.issues ? 'orange.400' : 'green.400'} hint="Reported issues" />
                </SimpleGrid>
            </Box>

            {/* Findings */}
            <Box bg={cardBg} borderWidth="1px" borderColor={bd} borderRadius="xl" p={4} role="region" aria-label="FlowSense twin findings">
                <HStack mb={2}><Icon as={FaExclamationTriangle} color="orange.400" /><Text fontWeight="800" color={heading}>What needs attention</Text><Badge>{findings.length}</Badge></HStack>
                <VStack align="stretch" spacing={2}>
                    {findings.slice(0, 10).map((f) => (
                        <Flex key={f.id} p={2} bg={tile} borderRadius="lg" borderLeftWidth="4px" borderLeftColor={`${LEVEL[f.level] || 'gray'}.400`} gap={2} align="center">
                            <Box flex={1} minW={0}>
                                <HStack spacing={1} flexWrap="wrap"><Badge colorScheme={LEVEL[f.level]} fontSize="2xs">{f.level}</Badge>{f.ml && <Badge colorScheme="purple" fontSize="2xs">ML</Badge>}<Text fontSize="sm" fontWeight="700">{f.title}</Text></HStack>
                                <Text fontSize="xs" color={muted} noOfLines={3}>{f.detail}</Text>
                            </Box>
                            {f.action && onAction && (
                                <Tooltip label={f.action.type === 'sensor' ? 'Open the sensor' : 'Fly to it on the map'} hasArrow>
                                    <IconButton size="sm" variant="ghost" colorScheme="purple" icon={<Icon as={f.action.type === 'sensor' ? FaSatellite : FaCrosshairs} />} aria-label={`Go to: ${f.title}`} onClick={() => onAction(f.action)} />
                                </Tooltip>
                            )}
                        </Flex>
                    ))}
                    {!findings.length && <Text fontSize="sm" color={muted}>{s ? 'Nothing needs attention right now.' : 'Loading…'}</Text>}
                </VStack>
            </Box>

            {/* Risk ranking */}
            <Box bg={cardBg} borderWidth="1px" borderColor={bd} borderRadius="xl" p={4} role="region" aria-label="Asset risk ranking">
                <HStack mb={1}><Icon as={FaTint} color="blue.400" /><Text fontWeight="800" color={heading}>Asset risk ranking</Text></HStack>
                <Text fontSize="xs" color={muted} mb={2}>ML failure probability, remaining life, live readings, alerts, issues and sensor anomalies — every point has a reason.</Text>
                <VStack align="stretch" spacing={1.5}>
                    {shownRisks.map((r, i) => (
                        <Flex key={r.id} align="center" gap={2} p={1.5} borderRadius="md" _hover={{ bg: tile }} cursor={onAction ? 'pointer' : 'default'} onClick={() => onAction?.({ type: 'asset', id: r.id, ...(r.coords || {}) })} role="button" aria-label={`${r.name} risk ${r.score}`}>
                            <Text fontSize="xs" w="18px" color={muted}>{i + 1}</Text>
                            <Box flex={1} minW={0}>
                                <Text fontSize="sm" fontWeight="700" noOfLines={1}>{r.name}</Text>
                                <Text fontSize="2xs" color={muted} noOfLines={1}>{r.drivers.slice(0, 2).map((x) => x.label).join(' · ') || '—'}</Text>
                            </Box>
                            <Badge colorScheme={r.color} borderRadius="full" minW="40px" textAlign="center">{r.score}</Badge>
                        </Flex>
                    ))}
                    {!risks.length && <Text fontSize="sm" color={muted}>No asset has enough data to score yet.</Text>}
                    {risks.length > 6 && <Button size="xs" variant="ghost" onClick={() => setShowAllRisks((x) => !x)}>{showAllRisks ? 'Show fewer' : `Show ${Math.min(25, risks.length)} of ${risks.length}`}</Button>}
                </VStack>
            </Box>

            {/* Explainer */}
            <Box bg={aiBg} borderWidth="1px" borderColor={bd} borderRadius="xl" p={4} role="region" aria-label="FlowSense AI explainer">
                <HStack mb={2}><Icon as={FaBrain} color="purple.400" /><Text fontWeight="800" color={heading}>Ask FlowSense</Text></HStack>
                <Wrap spacing={2} mb={2}>
                    <WrapItem><Button size="xs" leftIcon={<Icon as={FaGraduationCap} />} colorScheme="purple" variant="outline" onClick={() => push({ q: 'Explain this screen', answer: explainTwinScreen(intel), source: 'guide' })}>Explain this screen</Button></WrapItem>
                    <WrapItem><Button size="xs" leftIcon={<Icon as={FaUniversity} />} colorScheme="purple" variant="outline" onClick={() => push({ q: 'For the Ministry of Works', answer: explainTwinForMinistry(intel), source: 'guide' })}>For the Ministry of Works</Button></WrapItem>
                    {(selectedAsset || selectedSensor) && <WrapItem><Button size="xs" colorScheme="purple" variant="outline" onClick={explainSelection}>Explain selection</Button></WrapItem>}
                </Wrap>
                <Wrap spacing={1} mb={2}>
                    {TWIN_QUESTIONS.map((x) => <WrapItem key={x}><Button size="xs" variant="ghost" colorScheme="purple" onClick={() => ask(x)} isDisabled={asking}>{x}</Button></WrapItem>)}
                </Wrap>
                <HStack role="search">
                    <Input size="sm" bg={cardBg} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ask(q); } }} placeholder="Ask about leaks, sensors, assets, risk…" aria-label="Ask FlowSense about the digital twin" maxLength={500} borderRadius="lg" />
                    <IconButton size="sm" colorScheme="purple" icon={asking ? <Spinner size="xs" /> : <Icon as={FaPaperPlane} />} onClick={() => ask(q)} isDisabled={asking || !q.trim()} aria-label="Send question" />
                </HStack>
                <VStack align="stretch" spacing={2} mt={3} aria-live="polite">
                    {history.map((h) => (
                        <Box key={h.at} bg={cardBg} p={3} borderRadius="lg" position="relative" borderWidth="1px" borderColor={bd}>
                            <HStack justify="space-between" mb={1} pr={12}>
                                <Text fontSize="xs" fontWeight="700" noOfLines={1}>{h.q}</Text>
                                <Badge colorScheme={h.source === 'ai' ? 'purple' : 'gray'} fontSize="2xs">{h.source === 'ai' ? `FlowSense AI${h.model ? ` · ${h.model}` : ''}` : h.source === 'guide' ? 'Guide' : 'FlowSense rules'}</Badge>
                            </HStack>
                            <HStack position="absolute" top={1} right={1} spacing={0}>
                                <IconButton size="xs" variant="ghost" icon={<Icon as={FaCopy} />} aria-label="Copy answer" onClick={() => copy(h.answer)} />
                                <IconButton size="xs" variant="ghost" icon={<Icon as={FaTimes} />} aria-label="Close answer" onClick={() => setHistory((x) => x.filter((y) => y.at !== h.at))} />
                            </HStack>
                            <Text fontSize="sm" whiteSpace="pre-wrap">{h.answer}</Text>
                        </Box>
                    ))}
                </VStack>
            </Box>
        </VStack>
    );
}

TwinAiExplainer.propTypes = {
    intel: PropTypes.object, selectedAsset: PropTypes.object, selectedSensor: PropTypes.object, isLive: PropTypes.bool,
    historicalTimestamp: PropTypes.any, simulation: PropTypes.object, onAction: PropTypes.func,
};
export default memo(TwinAiExplainer);
